import { motion } from "framer-motion";

export default function TermsPage() {
  return (
    <main data-testid="terms-page" className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-16 px-6 md:px-12 lg:px-24 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-4"
          >
            Legal
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl text-white mb-4"
          >
            Terms of Service
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-slate-500 text-sm"
          >
            Last updated: March 2025
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto prose prose-invert prose-slate">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-white text-2xl mb-4">1. Acceptance of Terms</h2>
              <p className="text-slate-400 leading-relaxed">
                By accessing and using the Unleashed Ventures website ("Site"), you accept and agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Site. These Terms apply to all visitors, users, and others who access or use the Site.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">2. Description of Services</h2>
              <p className="text-slate-400 leading-relaxed">
                Unleashed Ventures is a strategic investment firm specializing in micro-cap companies and private entity acquisitions. Our Site provides information about our company, investment portfolio, and related services. The content on this Site is for informational purposes only and does not constitute investment advice, financial advice, trading advice, or any other sort of advice.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">3. Investment Disclaimer</h2>
              <p className="text-slate-400 leading-relaxed mb-4">
                The information provided on this Site regarding our portfolio companies and investments is for informational purposes only and should not be construed as:
              </p>
              <ul className="text-slate-400 space-y-2 list-disc list-inside">
                <li>An offer to sell or solicitation of an offer to buy any securities</li>
                <li>Investment advice or recommendation to purchase or sell any security</li>
                <li>A guarantee of future performance or returns</li>
                <li>An endorsement or recommendation of any particular investment strategy</li>
              </ul>
              <p className="text-slate-400 leading-relaxed mt-4">
                Past performance is not indicative of future results. All investments involve risk, including the potential loss of principal. You should consult with qualified financial, legal, and tax advisors before making any investment decisions.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">4. Intellectual Property</h2>
              <p className="text-slate-400 leading-relaxed">
                The Site and its entire contents, features, and functionality (including but not limited to all information, software, text, displays, images, video, and audio, and the design, selection, and arrangement thereof) are owned by Unleashed Ventures, its licensors, or other providers of such material and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">5. User Conduct</h2>
              <p className="text-slate-400 leading-relaxed mb-4">
                You agree not to use the Site:
              </p>
              <ul className="text-slate-400 space-y-2 list-disc list-inside">
                <li>In any way that violates any applicable federal, state, local, or international law or regulation</li>
                <li>To transmit any advertising or promotional material without our prior written consent</li>
                <li>To impersonate or attempt to impersonate Unleashed Ventures, an employee, or any other person or entity</li>
                <li>To engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Site</li>
                <li>To attempt to gain unauthorized access to any portion of the Site or any systems or networks connected to the Site</li>
              </ul>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">6. Third-Party Links</h2>
              <p className="text-slate-400 leading-relaxed">
                The Site may contain links to third-party websites or services that are not owned or controlled by Unleashed Ventures. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services. You acknowledge and agree that Unleashed Ventures shall not be responsible or liable for any damage or loss caused by the use of any such content or services.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">7. Disclaimer of Warranties</h2>
              <p className="text-slate-400 leading-relaxed">
                THE SITE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT ANY WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. UNLEASHED VENTURES DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SITE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">8. Limitation of Liability</h2>
              <p className="text-slate-400 leading-relaxed">
                IN NO EVENT SHALL UNLEASHED VENTURES, ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SITE.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">9. Indemnification</h2>
              <p className="text-slate-400 leading-relaxed">
                You agree to defend, indemnify, and hold harmless Unleashed Ventures and its licensees and licensors, and their employees, contractors, agents, officers, and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs, or debt, and expenses arising from your use of and access to the Site, or your violation of these Terms.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">10. Governing Law</h2>
              <p className="text-slate-400 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located within the United States.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">11. Changes to Terms</h2>
              <p className="text-slate-400 leading-relaxed">
                We reserve the right to modify or replace these Terms at any time at our sole discretion. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. By continuing to access or use our Site after those revisions become effective, you agree to be bound by the revised terms.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">12. Contact Information</h2>
              <p className="text-slate-400 leading-relaxed">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="mt-4 p-6 bg-slate-900/50 border border-white/5">
                <p className="text-white font-medium">Unleashed Ventures</p>
                <p className="text-slate-400 mt-2">Email: legal@unleashedventures.com</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
